import { NextWebVitalsMetric } from 'next/app';
import { v4 as uuid } from 'uuid';
import Cookies from 'js-cookie';
import getConfig from 'next/config';
import { isProduction } from '@components/Utils';
import { ErrorSource } from '@components/Service/ContentForm.utils';

export enum MetricEvent {
  CONTENT_SAVE_ERROR = 'church.life.chop.content_save_error.v1_0',
  CONTENT_FORM_VIEWED = 'church.life.chop.content_form_viewed.v1_0',
  CONTENT_SAVED = 'church.life.chop.content_saved.v2_0',
  HEADWAY_VIEWED = 'church.life.chop.headway_viewed_v1_0',
  HEARTBEAT = 'church.life.chop.heartbeat.v2_6',
  MOMENT_TEMPLATE_DELETED = 'church.life.chop.moment_template_deleted_v1_0',
  MOMENT_TEMPLATE_SAVED = 'church.life.chop.moment_template_saved_v1_0',
  PAGE_VIEW = 'church.life.chop.page_view.v2_0',
  PREVIEW_SERVICE_CREATED = 'church.life.chop.preview_service_created_v1_0',
  SERVICE_DELETED = 'church.life.chop.service_deleted_v1_0',
  SUBSCRIBER_DELETED = 'church.life.chop.subscriber_deleted_v1_0',
  SUBSCRIBER_PROFILE_UPDATED = 'church.life.chop.subscriber_profile_updated_v1_0',
  USER_SAVED = 'church.life.chop.user_saved_v2_0',
  USERS_EXPORTED = 'church.life.chop.users_exported_v1_0',
  WEB_VITAL = 'church.life.chop.web_vital_v1_0',
}

interface BaseData {
  service_id: string;
  reporting_lag: number;
  session_id: string;
  subscriber_id: string;
  organization_id: string;
  referrer: string;
  user_agent: string;
  location: string;
  client: string;
}

interface ContentFormErrorData extends BaseData {
  error: {
    property: string;
    message: string;
    source: ErrorSource;
  };
}

interface ContentSavedData extends BaseData {
  content_id: string;
}

interface HeartbeatData extends BaseData {
  interval: number;
}

interface MomentTemplateData extends BaseData {
  moment_template_id: string;
}

interface ServiceData extends BaseData {
  service_id: string;
}

interface SubscriberDeleteData extends BaseData {
  target_subscriber_id: string;
}

interface SubscriberProfileUpdatedData extends BaseData {
  target_subscriber_id: string;
}

interface UserSavedData extends BaseData {
  target_subscriber_id: string;
}

interface WebVitalsData extends BaseData {
  web_vital: NextWebVitalsMetric;
}

type PageViewData = BaseData;

type MetricData =
  | ContentFormErrorData
  | ContentSavedData
  | HeartbeatData
  | MomentTemplateData
  | PageViewData
  | ServiceData
  | SubscriberDeleteData
  | SubscriberProfileUpdatedData
  | UserSavedData
  | WebVitalsData;

interface MetricSchema {
  type: MetricEvent | string;
  id: string;
  source: string;
  specVersion: string;
  data: MetricData;
  contentType: string;
  time: string;
}

const timeouts: NodeJS.Timeout[] = [];
const intervals: NodeJS.Timeout[] = [];

const delay = (ms: number, handler: () => void): NodeJS.Timeout => setTimeout(handler, ms);
const { publicRuntimeConfig } = getConfig();

const getStagingEvent = (event: MetricEvent): string => {
  const eventParts = event.split('.');
  eventParts[2] = `${eventParts[2]}_staging`;
  return eventParts.join('.');
};

export const getEventData = (type: MetricEvent, data: MetricData): MetricSchema => ({
  type: isProduction() ? type : getStagingEvent(type),
  id: uuid(),
  source: window.location.origin,
  specVersion: '0.2',
  contentType: 'application/json',
  data,
  time: new Date().toISOString(),
});

const send = async (type: MetricEvent, data: MetricData): Promise<void> => {
  const eventData: MetricSchema = getEventData(type, data);
  if (publicRuntimeConfig.METRICS_HOST) {
    const url = publicRuntimeConfig.METRICS_HOST;
    const body = JSON.stringify(eventData);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, body);
    } else {
      fetch(url, { body, method: 'POST', keepalive: true });
    }
  }
};

export const getBaseData = (): BaseData => {
  return {
    reporting_lag: 0,
    service_id: '',
    session_id: Cookies.get('SESSION_ID') || '',
    subscriber_id: sessionStorage.getItem('SUBSCRIBER_ID') || '',
    organization_id: sessionStorage.getItem('ORGANIZATION_ID') || '',
    referrer: sessionStorage.getItem('REFERRER') || '',
    user_agent: window.navigator.userAgent,
    location: window.location.href,
    client: 'chop-admin-client',
  };
};

const startHeartbeat = async (): Promise<void> => {
  while (timeouts.length) {
    const timeoutId = timeouts.pop();
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }

  while (intervals.length) {
    const intervalId = intervals.pop();
    if (intervalId) {
      clearInterval(intervalId);
    }
  }

  await send(MetricEvent.HEARTBEAT, {
    ...getBaseData(),
    interval: 0,
  });

  timeouts.push(
    delay(3000, () =>
      send(MetricEvent.HEARTBEAT, {
        ...getBaseData(),
        interval: 3,
      })
    )
  );

  timeouts.push(
    delay(10000, () =>
      send(MetricEvent.HEARTBEAT, {
        ...getBaseData(),
        interval: 10,
      })
    )
  );

  timeouts.push(
    delay(30000, () =>
      send(MetricEvent.HEARTBEAT, {
        ...getBaseData(),
        interval: 30,
      })
    )
  );

  // 1 minute intervals
  intervals.push(
    setInterval(async () => {
      await send(MetricEvent.HEARTBEAT, {
        ...getBaseData(),
        interval: 60,
      });
    }, 60000)
  );
};

const pageView = (): Promise<void> => send(MetricEvent.PAGE_VIEW, getBaseData());

const contentFormError = (property: string, message: string, source: ErrorSource): Promise<void> =>
  send(MetricEvent.CONTENT_SAVE_ERROR, {
    ...getBaseData(),
    error: { source, property, message },
  });

const contentSaved = (content_id: string): Promise<void> =>
  send(MetricEvent.CONTENT_SAVED, {
    ...getBaseData(),
    content_id,
  });

const userSaved = (target_subscriber_id: string): Promise<void> =>
  send(MetricEvent.USER_SAVED, {
    ...getBaseData(),
    target_subscriber_id,
  });

const serviceDeleted = (service_id: string): Promise<void> =>
  send(MetricEvent.SERVICE_DELETED, {
    ...getBaseData(),
    service_id,
  });

const subscriberDeleted = (target_subscriber_id: string): Promise<void> =>
  send(MetricEvent.SUBSCRIBER_DELETED, {
    ...getBaseData(),
    target_subscriber_id,
  });

const subscriberUpdated = (target_subscriber_id: string): Promise<void> =>
  send(MetricEvent.SUBSCRIBER_PROFILE_UPDATED, {
    ...getBaseData(),
    target_subscriber_id,
  });

const momentTemplateSaved = (moment_template_id: string): Promise<void> =>
  send(MetricEvent.MOMENT_TEMPLATE_SAVED, {
    ...getBaseData(),
    moment_template_id,
  });

const momentTemplateDeleted = (moment_template_id: string): Promise<void> =>
  send(MetricEvent.MOMENT_TEMPLATE_DELETED, {
    ...getBaseData(),
    moment_template_id,
  });

const webVital = (metric: NextWebVitalsMetric): Promise<void> =>
  send(MetricEvent.WEB_VITAL, {
    ...getBaseData(),
    web_vital: metric,
  });

const previewServiceCreated = (service_id: string): Promise<void> =>
  send(MetricEvent.PREVIEW_SERVICE_CREATED, {
    ...getBaseData(),
    service_id,
  });

const usersExported = (): Promise<void> => send(MetricEvent.USERS_EXPORTED, getBaseData());

const headwayViewed = (): Promise<void> => send(MetricEvent.HEADWAY_VIEWED, getBaseData());

export const Metrics = {
  contentFormError,
  contentSaved,
  headwayViewed,
  momentTemplateDeleted,
  momentTemplateSaved,
  pageView,
  previewServiceCreated,
  serviceDeleted,
  startHeartbeat,
  subscriberDeleted,
  subscriberUpdated,
  userSaved,
  usersExported,
  webVital,
};
